import { Link } from 'react-router-dom';
import '../static/App.css';
import image3 from "../images/image3.jpeg";
import image4 from "../images/image4.jpeg";
import image6 from "../images/image6.jpg";
import { Button, Box, Typography, Grid, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useState, useEffect } from "react";
import { Favorite, Accessibility, Home as HomeIcon, MedicalServices } from '@mui/icons-material';

const Home = () => {
    const sections = [
        { 
            title: 'MISSION', 
            description: 'Respectfully and compassionately meet the needs of our clients and their families by listening, planning, educating, and delivering the highest quality of individualized care.' 
        },
        { 
            title: 'VISION', 
            description: 'Create and sustain a leading high-quality service that supports individuals to live as independently as possible in the comfort of their own homes.' 
        },
        { 
            title: 'PROMISE', 
            description: '24/7 AssureCare LLC provides high-quality care services to individuals needing assistance with daily living activities. We specialize in care for seniors, individuals with disabilities, and those recovering from illness or surgery.' 
        },
    ];

    const needs = [
        'In need of aid post-operation, hospitalization, or rehabilitation',
        'Lives alone and is at risk of falls or injury',
        'Disabled or uses a wheelchair, walker or cane',
        'In need of care with daily living activities'
    ];

    const services = [
        {
            icon: <HomeIcon sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'In-Home Care',
            description: 'Comprehensive care in the comfort of your own home'
        },
        {
            icon: <Accessibility sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Mobility Assistance',
            description: 'Support with movement and daily activities'
        },
        {
            icon: <MedicalServices sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Medical Support',
            description: 'Medication management and health monitoring'
        },
        {
            icon: <Favorite sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Companion Care',
            description: 'Emotional support and social engagement'
        }
    ];

    const [index, setIndex] = useState(0);
    const [fade, setFade] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const interval = setInterval(() => {
            setFade(false); 
            setTimeout(() => {
                setIndex((prev) => (prev + 1) % sections.length);
                setFade(true);
            }, 500);
        }, 11000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className='page-back'>
            <Helmet>
                <title>24/7 AssureCare - Home</title>
                <meta name="description" content="24/7 AssureCare LLC provides high-quality home care services in Cook, DuPage, and Lake counties. Our mission is compassionate, individualized care for seniors and those with disabilities." />
                <meta name="keywords" content="home care, senior care, disability care, in-home assistance, 24/7 care, healthcare services, post-operation aid, rehabilitation support" />
                
            </Helmet>
            
            <Box className="container" sx={{ py: 4 }}>
                {/* Hero Section */}
                <Box sx={{ position: 'relative', height: isMobile ? '400px' : '500px', width: '100%', mb: 6,
                    borderRadius: 3,overflow: 'hidden',boxShadow: 3}}>
                    <img src={image3} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Compassionate caregiving"/>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                        backgroundColor: 'rgba(0,0,0,0.4)',display: 'flex',flexDirection: 'column',justifyContent: 'center',
                        alignItems: 'center',textAlign: 'center',color: 'white',p: 3}}>
                        <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', mb: 2,fontSize: isMobile ? '2rem' : '2.8rem',textShadow: '2px 2px 4px rgba(0,0,0,0.7)'}}>
                            PROVIDING EXCELLENT CARE YOU DESERVE
                        </Typography>
                        <Typography variant="h5" sx={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)',fontSize: isMobile ? '1.2rem' : '1.5rem'}}>OUR SERVICES ARE IN THE FOLLOWING COUNTIES:<br />
                            Cook - DuPage - Lake
                        </Typography>
                        <Typography variant="body2" sx={{ color:'white', fontStyle:'italic', mt:1 }}>(If you're outside these areas, contact us — we may still be able to help!)</Typography>
                    </Box>
                </Box>

                {/* MVP Section */}
                <Card sx={{ mb: 6, borderRadius: 3, boxShadow: 3, textAlign: 'center', p: 4}}>
                    <CardContent>
                        <Typography variant="h3" component="h2" sx={{ color: '#1b8fc9', mb: 3, fontWeight: 'bold'}}>
                            YOU ARE OUR M.V.P.
                        </Typography>
                        
                        <Box sx={{ minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center',mb: 3}}>
                            <Box className={`fade-slide ${fade ? "fade-in" : "fade-out"}`}>
                                <Typography variant="h4" component="h3" sx={{ color: '#1b8fc9', mb: 2,fontWeight: 'bold'}}>
                                    {sections[index].title}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6,maxWidth: '800px',margin: '0 auto'}}>
                                    {sections[index].description}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Button variant='contained' component={Link} to='/AboutUs'
                            sx={{ backgroundColor: '#1b8fc9', '&:hover': { color: 'white', backgroundColor: '#156a9e' }, px: 4, py: 1.5, fontWeight: 'bold'}}>
                            Learn More About Us
                        </Button>
                    </CardContent>
                </Card>

                {/* Services Overview */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h3" component="h2" sx={{ textAlign: 'center', mb: 4, color: 'rgba(255, 255, 255, 1)',fontWeight: 'bold'}}>
                        Our Care Services
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {services.map((service, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{ height: '100%', textAlign: 'center', p: 3,transition: 'transform 0.3s, box-shadow 0.3s','&:hover': {transform: 'translateY(-5px)',boxShadow: 4}}}>
                                    <CardContent>
                                        <Box sx={{ mb: 2 }}>
                                            {service.icon}
                                        </Box>
                                        <Typography variant="h6" component="h3" sx={{ mb: 2,color: '#1b8fc9', fontWeight: 'bold'}}>
                                            {service.title}
                                        </Typography>
                                        <Typography variant="body2">
                                            {service.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Needs Section */}
                <Grid container spacing={3} sx={{ mb: 6 }}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ borderRadius: { xs: '10px 10px 0 0', md: '10px 0 0 10px' }, overflow: 'hidden', height: '100%',boxShadow: 3}}>
                            <img src={image6} style={{ width: "100%", height: isMobile ? "250px" : "100%", objectFit: "cover"}} alt="Caregiver providing assistance"/>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Card sx={{ height: '100%', borderRadius: { xs: 0, md: 0 },boxShadow: 3}}>
                            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="h4" component="h2" sx={{ color: "#1b8fc9", mb: 3,fontWeight: 'bold'}}>
                                    WHO NEEDS OUR SUPPORTIVE SERVICES?
                                </Typography>
                                
                                <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 3 }}>
                                    {needs.map((need, index) => (
                                        <Typography key={index} component="li" variant="body1" sx={{ mb: 2,fontSize: '1.1rem'}}>
                                            {need}
                                        </Typography>
                                    ))}
                                </Box>
                                
                                <Button variant="contained" component={Link}to="/Services"sx={{ backgroundColor: "#1b8fc9",'&:hover': { color: 'white', backgroundColor: '#156a9e' },px: 4,py: 1.5,fontWeight: 'bold'}}>
                                    Explore Our Services
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ borderRadius: { xs: '0 0 10px 10px', md: '0 10px 10px 0' }, overflow: 'hidden', height: '100%',boxShadow: 3}}>
                            <img src={image4} style={{ width: "100%", height: isMobile ? "250px" : "100%", objectFit: "cover"}} alt="Caregiver at hospital appointment" />
                        </Box>
                    </Grid>
                </Grid>

                {/* Call to Action */}
                <Card sx={{ backgroundColor: 'rgba(21,149,160,0.1)', borderRadius: 3, p: 4, textAlign: 'center',boxShadow: 3}}>
                    <CardContent>
                        <Typography variant="h4" component="h2" sx={{ mb: 2, color: 'rgba(0, 0, 0, 1)',fontWeight: 'bold'}}>
                            Ready to Get Started?
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem',maxWidth: '600px', margin: '0 auto'}}>
                            Contact us today to discuss your care needs and how we can help you or your loved one maintain independence and quality of life.
                        </Typography>
                        <Button variant="contained" className='mt-3 pb-2' component={Link}to="/Contact"sx={{ backgroundColor: '#1b8fc9', '&:hover': { color: 'white', backgroundColor: '#156a9e' }, px: 4, py: 1.5, fontWeight: 'bold'}}>
                            Schedule a Consultation
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        </div>
    );
};

export default Home;